import { mapGetters } from 'vuex';
import useVuelidate from '@vuelidate/core';
import _cloneDeep from 'lodash/cloneDeep';

import {
  GET_PAYMENT_METHOD_MAPPING,
  GET_PAYMENT_TIMING_MAPPING,
} from '@/store/store/constants';
import districtInfo from '@/utils/taiwanDistricts';
import {
  isUrl,
  phone,
  required,
  requiredIf,
} from '@/utils/validators';
import { STORE_MODES } from '@/utils/constants';

const emptyStore = {
  emails: [],
  store_detail: {
    name: '',
    city: '',
    dist: '',
    address: '',
    contact_person: '',
    note: '',
  },
  store_tags: [],
  store_close_days: [],
  store_payment_way: {
    is_cash: false,
    is_remittance: false,
    is_instant: false,
    is_every_month: false,
    is_prepaid: false,
  },
  store_order_way: {
    telephone: '',
    phone: '',
    line_account: '',
    line_url: '',
    iorder_url: '',
    messenger_name: '',
  },
};

const orderMethodMapping = {
  line: {
    label: '官方帳號',
    icon: '',
    key: 'line_url',
    placeholder: '@',
  },
  iding: {
    label: '微碧愛點餐',
    icon: '',
    key: 'iorder_url',
    placeholder: 'https://',
  },
  facebook: {
    label: 'Facebook',
    icon: '',
    key: 'facebook_url',
    placeholder: 'https://',
  },
  phone: {
    label: '電話',
    icon: '',
    key: 'phone',
    placeholder: '',
  },
};

export default {
  name: 'StoreForm',
  setup() {
    return { v$: useVuelidate() };
  },
  props: {
    store: {
      type: Object,
      default: () => emptyStore,
      validator(store) {
        const requiredKeys = ['store_detail', 'store_payment_way', 'store_order_way'];
        return requiredKeys.every((key) => key in store);
      },
    },
  },
  data() {
    return {
      currentStore: emptyStore,
      districtInfo,
      orderMethodMapping,
      paymentMethods: [],
      showAdvancedSetting: false,
    };
  },
  watch: {
    store(newValue) {
      this.paymentMethods = [];
      this.initializeStore(newValue);
    },
    currentStore: {
      handler() { this.v$.$touch(); },
      deep: true,
    },
    isEditMode: {
      handler() { this.initializeStore(this.store); },
    },
  },
  mounted() {
    this.initializeStore(this.store);
  },
  computed: {
    isCreateMode() {
      return this.$route.params.action === STORE_MODES.CREATE;
    },
    isEditMode() {
      return this.$route.params.action === STORE_MODES.EDIT;
    },
    invalidName() {
      const target = this.v$.currentStore.store_detail.name;
      return target.$dirty && target.$invalid;
    },
    invalidPaymentMethod() {
      const target = this.v$.paymentMethods;
      return target.$dirty && target.$invalid;
    },
    paymentMethodErrors() {
      const paymentMethodKeys = Object
        .values(this.orderMethodMapping)
        .map((method) => method.key);
      const filtered = Object
        .keys(this.v$.currentStore.store_order_way)
        .filter((key) => paymentMethodKeys.includes(key))
        .reduce((acc, key) => {
          acc[key] = this.v$.currentStore.store_order_way[key].$errors?.[0] || {};
          return acc;
        }, {});
      return filtered;
    },
    storeNameErrorMessage() {
      return this.v$.currentStore.store_detail.name.$errors?.[0]?.$message || '';
    },
    paymentMethodErrorMessage() {
      return this.v$.paymentMethods.$errors?.[0]?.$message || '';
    },
    currentDistrict() {
      const city = this.currentStore?.store_detail?.city;
      if (!city) return [];

      return this.districtInfo.find((c) => c.name === city)?.districts;
    },
    closedDays: {
      get() {
        return this.currentStore.store_close_days.map((d) => d.day);
      },
      set(value) {
        this.currentStore.store_close_days = value.map((d) => ({ day: d }));
      },
    },
    storeTags: {
      get() {
        return this.currentStore.store_tags.map((t) => t.name);
      },
      set(value) {
        this.currentStore.store_tags = value.map((t) => ({ name: t }));
      },
    },
    ...mapGetters('store', {
      paymentMethodMapping: GET_PAYMENT_METHOD_MAPPING,
      paymentTimingMapping: GET_PAYMENT_TIMING_MAPPING,
    }),
  },
  methods: {
    beforeAddEmail(tag) {
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(tag);
    },
    initializeStore(store) {
      this.currentStore = _cloneDeep(store);
      if (this.isEditMode) {
        /*
          Since API give us inconsistent data structure.
          We need to do conversion before we can editing
        */

        /* Convert payment method */
        Object.keys(this.orderMethodMapping).forEach((methodName) => {
          const method = this.orderMethodMapping[methodName];
          if (this.currentStore.store_order_way[method.key]) {
            this.paymentMethods.push(methodName);
          }
        });
      }

      this.$nextTick(() => {
        this.v$.$reset();
      });
    },
    syncPaymentMethods(method) {
      if (!this.paymentMethods.includes(method)) {
        this.currentStore.store_order_way[this.orderMethodMapping[method].key] = '';
      }
    },
    timeConverter(time = 0) {
      const day = Math.floor(time / 1440);
      const hour = Math.floor((time - (day * 1440)) / 60);
      const minute = Math.round(time % 60);

      return { day, hour, minute };
    },
    saveStore() {
      this.v$.$reset();
      this.$emit('save', this.currentStore);
    },
    updateDistrict() {
      this.currentStore.store_detail.dist = this.currentDistrict[0].name;
    },
  },
  validations() {
    return {
      currentStore: {
        store_detail: {
          name: { required: required('店家名稱') },
        },
        store_order_way: {
          phone: {
            requiredIf: requiredIf('電話', this.paymentMethods.includes('phone')),
            phone,
          },
          iorder_url: {
            requiredIf: requiredIf('愛點餐網址', this.paymentMethods.includes('iding')),
            url: isUrl('愛點餐網址'),
          },
          line_url: {
            requiredIf: requiredIf('LINE 帳號', this.paymentMethods.includes('line')),
          },
          facebook_url: {
            requiredIf: requiredIf('Facebook 帳號', this.paymentMethods.includes('facebook')),
            url: isUrl('Facebook 帳號'),
          },
        },
      },
      paymentMethods: {
        required: required('訂購方式'),
      },
    };
  },
};
